import { getElementName } from '@shared/helpers/element.helpers';
import type { SortDirection } from '@shared/helpers/tree.helpers';
import {
  type ElementPath,
  getElementById,
  isProductListCategoryGroup,
  isProductListGroup,
  isProductListItem,
  isElement,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import {
  IProductListCategoryGroup,
  IProductListGroup,
  IProductListItem,
  OneOfSelectableProductListElements,
  ProductID,
  ProductRecord,
} from '@shared/models/product.model';
import {
  Baseline,
  OneOfProductListChildren,
  IProductElement,
} from '@shared/models/project.model';
import { getLast, isOneOf } from '@shared/helpers/array.helpers';
import { getKeys } from '@shared/helpers/object.helpers';
import {
  IElement,
  type ProductCategoryElement,
} from '@shared/models/project.model';
import { isCustomProductId } from '@shared/helpers/product.helpers';
import uniqBy from 'lodash/uniqBy';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import {
  getElementCategory,
  getElementCategoryAndSelectionsByProductId,
  isProductCategoryElement,
  isSystemCategoryElement,
} from '@shared/helpers/element-category.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { searchFilter } from '@shared/helpers/string.helpers';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import {
  quantityUnitToMathJSUnit,
  type Results,
} from '@shared/models/unit.model';
import { isQuantityUnit } from '@shared/helpers/unit.helpers';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { filterResultsByLifecycles } from '@shared/helpers/lifecycles.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { Lifecycle } from '@shared/models/lifecycles.model';
import { sortByAlternatives } from '@shared/models/sort.model';

export const PRODUCT_LIST_PATH_MAX_LENGTH = 60;
export const PRODUCT_ID_SEPARATOR = '__';
export const PRODUCT_LIST_EPD_NAME_SEPARATOR = ' - ';

/**
 * When a proposal is active, resolve each product element against the proposal-enriched
 * version so callers get the correct {@link IProductElement.product_id} and name rather
 * than the baseline value. Falls back to the baseline element when it is not found in the
 * resolved tree (e.g. element was removed by the proposal).
 */
export const resolveProductElementsForActiveProposal = (
  baselineElements: IProductElement[],
  resolvedVersion: Baseline | undefined,
): IProductElement[] => {
  if (!resolvedVersion) return baselineElements;
  return baselineElements.map((productElement) => {
    const resolved = getElementById(resolvedVersion, productElement.id, false);
    return isProductElement(resolved) ? resolved : productElement;
  });
};

/**
 * EPD row label parts: `[epdName, genericName?]`.
 */
export const getProductListEPDNames = (
  element: IProductListGroup,
  productLookup: ProductRecord,
): [epdName: string, genericName?: string] => {
  const epdName = getElementName(element, productLookup);
  if (!element.generic_product_id) {
    return [epdName];
  }
  const genericName = productLookup[element.generic_product_id]?.name;
  return [epdName, genericName];
};

/**
 * Display name for a product-list EPD group, e.g. `Hollowcore floor - Strängbetong HDF220 GSL`.
 */
export const getProductListGroupDisplayName = (
  element: IProductListGroup,
  productLookup: ProductRecord = {},
): string => {
  const [epdName, genericName] = getProductListEPDNames(element, productLookup);
  if (!genericName) return epdName;
  return `${genericName}${PRODUCT_LIST_EPD_NAME_SEPARATOR}${epdName}`;
};

/**
 * Name used when sorting product-list EPD groups (generic resource, not EPD label).
 */
export const getProductListGroupSortName = (
  element: IProductListGroup,
  productLookup: ProductRecord = {},
): string => {
  const [epdName, genericName] = getProductListEPDNames(element, productLookup);
  return genericName ?? epdName;
};

/**
 * Sort product-list EPD groups by generic resource name, then EPD name.
 */
export const compareProductListGroupsByName = (
  a: IProductListGroup,
  b: IProductListGroup,
  productLookup: ProductRecord,
): number => {
  const genericCompare = getProductListGroupSortName(
    a,
    productLookup,
  ).localeCompare(getProductListGroupSortName(b, productLookup));
  if (genericCompare !== 0) return genericCompare;
  const [aEpdName] = getProductListEPDNames(a, productLookup);
  const [bEpdName] = getProductListEPDNames(b, productLookup);
  return aEpdName.localeCompare(bEpdName);
};

export const sortProductListGroupsByName = <T extends IProductListGroup>(
  groups: T[],
  productLookup: ProductRecord,
): T[] =>
  [...groups].sort((a, b) =>
    compareProductListGroupsByName(a, b, productLookup),
  );

const PRODUCT_LIST_SORT_KEYS = getKeys(sortByAlternatives, false);

const PRODUCT_LIST_RESULT_FACTOR = {
  co2: 'co2e',
  cost: 'sek',
} as const;

type SortProductListOptions = {
  direction?: SortDirection;
  productLookup?: ProductRecord;
  /**
   * Lifecycle phases that count toward CO₂/cost order. Omit to use every phase
   * present on the row.
   */
  lifecycles?: Lifecycle[];
};

type ProductListSortableByResults = {
  results: Results;
};

/**
 * CO₂ or cost used to order a product-list row.
 * When {@link SortProductListOptions.lifecycles} is set, only those phases count
 * so the order matches the lifecycle-scoped bars.
 */
const getProductListResultSortValue = (
  results: Results,
  factor: 'co2e' | 'sek',
  lifecycles: Lifecycle[] | undefined,
): number => {
  const scoped = lifecycles
    ? filterResultsByLifecycles(results, lifecycles)
    : results;
  return getConversionFactorValue(scoped, factor);
};

/**
 * Sort rows by descending scoped CO₂ or cost. Values are computed once per row
 * so {@link filterResultsByLifecycles} does not run inside the comparator.
 */
const sortByDescendingResultValue = <T extends ProductListSortableByResults>(
  items: T[],
  factor: 'co2e' | 'sek',
  lifecycles: Lifecycle[] | undefined,
  direction: SortDirection = 'desc',
): T[] => {
  const keyed = items.map((item) => ({
    item,
    value: getProductListResultSortValue(item.results, factor, lifecycles),
  }));
  keyed.sort((a, b) =>
    direction === 'asc' ? a.value - b.value : b.value - a.value,
  );
  return keyed.map(({ item }) => item);
};

const sortProductListCategoryGroupsByName = (
  categories: IProductListCategoryGroupView[],
): IProductListCategoryGroupView[] =>
  [...categories].sort((a, b) => {
    const aName = getElementCategory(a.id)?.name ?? a.id;
    const bName = getElementCategory(b.id)?.name ?? b.id;
    return aName.localeCompare(bName);
  });

/**
 * Sort product-list EPD groups by the active list sort
 * ({@link sortByAlternatives}). Unknown keys keep input order. When
 * `lifecycles` is set, CO₂/cost order uses only those phases.
 */
export const sortProductListGroups = <T extends IProductListGroup>(
  groups: T[],
  sortBy: string,
  options: SortProductListOptions = {},
): T[] => {
  if (!isOneOf(PRODUCT_LIST_SORT_KEYS, sortBy)) return groups;
  const { productLookup = {}, lifecycles, direction } = options;
  switch (sortBy) {
    case 'position':
      return direction === 'desc' ? [...groups].reverse() : groups;
    case 'name':
      return [...groups].sort(
        (a, b) =>
          (direction === 'desc' ? -1 : 1) *
          compareProductListGroupsByName(a, b, productLookup),
      );
    case 'co2':
    case 'cost':
      return sortByDescendingResultValue(
        groups,
        PRODUCT_LIST_RESULT_FACTOR[sortBy],
        lifecycles,
        direction,
      );
    default:
      return assertUnreachable(sortBy);
  }
};

/**
 * Sort product-list category groups by the active list sort, then sort each
 * category's child product groups the same way. `position` and unknown keys
 * keep input order. When `lifecycles` is set, CO₂/cost order uses only those
 * phases so it matches the scoped bars.
 */
export const sortProductListCategoryGroups = (
  categories: IProductListCategoryGroupView[],
  sortBy: string,
  options: SortProductListOptions = {},
): IProductListCategoryGroupView[] => {
  if (!isOneOf(PRODUCT_LIST_SORT_KEYS, sortBy)) return categories;
  const { lifecycles, direction } = options;
  let sortedCategories: IProductListCategoryGroupView[];
  switch (sortBy) {
    case 'position':
      sortedCategories =
        direction === 'desc' ? [...categories].reverse() : categories;
      break;
    case 'name':
      sortedCategories = sortProductListCategoryGroupsByName(categories);
      if (direction === 'desc') sortedCategories.reverse();
      break;
    case 'co2':
    case 'cost':
      sortedCategories = sortByDescendingResultValue(
        categories,
        PRODUCT_LIST_RESULT_FACTOR[sortBy],
        lifecycles,
        direction,
      );
      break;
    default:
      return assertUnreachable(sortBy);
  }

  return sortedCategories.map((category) => ({
    ...category,
    elements: sortProductListGroups(category.elements, sortBy, options),
    visibleElements: sortProductListGroups(
      category.visibleElements,
      sortBy,
      options,
    ),
  }));
};

export const getProductListElementName = (
  element: OneOfProductListChildren,
  options: {
    disableTruncation?: boolean;
    productLookup?: ProductRecord;
  } = {},
) => {
  if (isProductListCategoryGroup(element)) {
    return getElementName(element, options.productLookup);
  }
  if (isProductListGroup(element)) {
    return getProductListGroupDisplayName(element, options.productLookup ?? {});
  }

  return getFormatedPath(
    element.path.filter(isElement).map((e) => getElementName(e)),
    options.disableTruncation,
  );
};

const getFormatedPath = (
  names: string[] = [],
  disableTruncation = false,
  separator = ' / ',
): string => {
  const first = names[0];
  const last = names[names.length - 1];
  const middle = ' / ... / ';

  const path = names.join(separator);

  if (
    !disableTruncation &&
    path.length > PRODUCT_LIST_PATH_MAX_LENGTH &&
    names.length > 2
  ) {
    return first + middle + last;
  }
  return path;
};

export const getProductElements = (
  element?: OneOfSelectableProductListElements,
): IProductElement[] => {
  const getProductElementFromItem = (
    item: IProductListItem,
  ): IProductElement | undefined => {
    const parent = getLast(item.path);
    if (!isElement(parent)) return undefined;
    const match = parent.elements?.find(
      (child): child is IProductElement =>
        isProductElement(child) && child.id === item.id,
    );
    return match;
  };

  if (isProductListCategoryGroup(element)) {
    return element.elements.flatMap((group) => getProductElements(group));
  }

  if (isProductListGroup(element)) {
    return element.elements
      .map(getProductElementFromItem)
      .filter(guards.is.defined);
  }

  if (isProductListItem(element)) {
    const resolved = getProductElementFromItem(element);
    return resolved ? [resolved] : [];
  }
  return [];
};

export const getProductElementOwners = (
  element: OneOfSelectableProductListElements,
): IElement[] => {
  if (isProductListCategoryGroup(element)) {
    return uniqBy(
      element.elements
        .flatMap((group) => group.elements.map((item) => getLast(item.path)))
        .filter(isElement),
      'id',
    );
  }

  if (isProductListGroup(element)) {
    return uniqBy(
      element.elements.map((item) => getLast(item.path)).filter(isElement),
      'id',
    );
  }

  if (isProductListItem(element)) {
    const parent = getLast(element.path);
    return isElement(parent) ? [parent] : [];
  }

  return [];
};

/**
 * Find the nearest product category element in the given element path.
 */
export const getProductCategoryElementFromPath = (
  path: ElementPath,
): ProductCategoryElement | undefined => {
  for (let i = path.length - 1; i >= 0; i -= 1) {
    const item = path[i];
    if (isProductCategoryElement(item)) return item;
  }
  return undefined;
};

/**
 * Category that represents where a product-list leaf sits.
 * Prefers the nearest system element (slab, column, wall, …). When the product
 * is not inside a system element, uses the product/material category instead.
 */
export const getProductListItemLocationCategoryId = (
  item: IProductListItem,
): ElementCategoryID => {
  const systemParent = item.path.findLast(isSystemCategoryElement);
  if (isSystemCategoryElement(systemParent)) return systemParent.category_id;

  const productCategory = getProductCategoryElementFromPath(item.path);
  if (productCategory) return productCategory.category_id;

  return (
    getElementCategoryAndSelectionsByProductId(item.product_id)?.category_id ??
    ElementCategoryID.OtherProduct
  );
};

/**
 * Resolve a single product category element from a list selection.
 * Returns undefined when no category exists or when selection contains multiple product categories.
 */
export const getProductCategoryElementFromSelection = (
  element: OneOfSelectableProductListElements,
): ProductCategoryElement | undefined => {
  const resolveCategoryFromPaths = (
    paths: ElementPath[],
  ): ProductCategoryElement | undefined => {
    const categoryElements = paths
      .map(getProductCategoryElementFromPath)
      .filter(guards.is.defined);
    if (categoryElements.length === 0) return undefined;
    const uniqueCategoryIds = new Set(
      categoryElements.map((item) => item.category_id),
    );
    return uniqueCategoryIds.size === 1 ? categoryElements[0] : undefined;
  };

  if (isProductListItem(element)) {
    return resolveCategoryFromPaths([element.path]);
  }

  if (isProductListGroup(element)) {
    return resolveCategoryFromPaths(element.elements.map((item) => item.path));
  }

  if (isProductListCategoryGroup(element)) {
    return resolveCategoryFromPaths(
      element.elements.flatMap((group) =>
        group.elements.map((item) => item.path),
      ),
    );
  }
};

/**
 * Get the product id for the product list element.
 * If the product is an epd / custom product, the id will be prefixed with the epd id.
 * Else the id will be the generic product id.
 *
 * Examples:
 *
 * id = 'custom_123', gid = 'boverket_456' -> 'custom_123__boverket_456'
 *
 * id = 'abc_123', gid = 'boverket_456' -> 'boverket_456'
 *
 * id = 'boverket_123', gid = undefined -> 'boverket_123'
 */
export const getProductListElementId = (
  id: ProductID,
  gid: ProductID | undefined,
) => {
  const epdId = isCustomProductId(id) ? id : undefined;
  const genericId = gid ?? id;

  return epdId && epdId !== genericId
    ? epdId + PRODUCT_ID_SEPARATOR + genericId
    : genericId;
};

/**
 * Search-aware view of an {@link IProductListGroup}. `elements` keeps the
 * full membership so the group's CO₂/cost bar (driven by per-item proposal
 * records summed in row components) stays stable while the user types;
 * `visibleElements` is the search-filtered subset to render.
 */
export interface IProductListGroupView extends IProductListGroup {
  visibleElements: IProductListItem[];
}

/**
 * Search-aware view of an {@link IProductListCategoryGroup}. `elements` keeps
 * the full set of product groups (each with full items) so bar totals stay
 * stable; `visibleElements` is the search-filtered subset to render.
 */
export interface IProductListCategoryGroupView extends Omit<
  IProductListCategoryGroup,
  'elements'
> {
  elements: IProductListGroupView[];
  visibleElements: IProductListGroupView[];
}

const wrapGroupAsView = (group: IProductListGroup): IProductListGroupView => ({
  ...group,
  visibleElements: group.elements,
});

const wrapCategoryAsView = (
  category: IProductListCategoryGroup,
): IProductListCategoryGroupView => {
  const groupViews = category.elements.map(wrapGroupAsView);
  return { ...category, elements: groupViews, visibleElements: groupViews };
};

/**
 * Filter product groups (flat) by search string. An item matches if its
 * display name matches. A group is kept if its own name matches (all items
 * visible) or if any of its items match (only matching items visible). The
 * returned groups always carry the full `elements` array so bar totals are
 * driven by the group's true contents — only `visibleElements` narrows.
 */
export const filterProductListGroups = (
  groups: IProductListGroup[],
  search: string,
  productLookup?: ProductRecord,
): IProductListGroupView[] => {
  if (!search.trim()) return groups.map(wrapGroupAsView);

  return groups.flatMap((group) => {
    const groupName = getProductListElementName(group, { productLookup });
    if (searchFilter(groupName, search)) return [wrapGroupAsView(group)];

    const matchingItems = group.elements.filter((item) =>
      searchFilter(
        getProductListElementName(item, {
          productLookup,
          disableTruncation: true,
        }),
        search,
      ),
    );
    if (matchingItems.length === 0) return [];
    return [{ ...group, visibleElements: matchingItems }];
  });
};

/**
 * Filter product category groups (nested) by search string. A category is
 * kept if its name matches (all children visible) or any of its product
 * groups/items match (filtered down to matches in `visibleElements`). The
 * returned categories always carry the full `elements` array so the
 * category bar's totals reflect the true group membership.
 */
export const filterProductListCategoryGroups = (
  categoryGroups: IProductListCategoryGroup[],
  search: string,
  productLookup?: ProductRecord,
): IProductListCategoryGroupView[] => {
  if (!search.trim()) return categoryGroups.map(wrapCategoryAsView);

  return categoryGroups.flatMap((category) => {
    const categoryName = getElementCategory(category.id)?.name ?? category.id;
    if (searchFilter(categoryName, search)) {
      return [wrapCategoryAsView(category)];
    }

    const visibleGroups = filterProductListGroups(
      category.elements,
      search,
      productLookup,
    );
    if (visibleGroups.length === 0) return [];

    const allGroups = category.elements.map(wrapGroupAsView);
    return [
      { ...category, elements: allGroups, visibleElements: visibleGroups },
    ];
  });
};

/**
 * Pre-format a product list row's total quantity so the unit auto-folds
 * (e.g. mm → m) when possible. Falls back to plain number formatting + raw
 * unit when the unit isn't a {@link QuantityUnit}.
 */
export const formatProductRowQuantity = (
  totalQuantity: number | undefined,
  unit: string,
): {
  quantityPlaceholder: string | undefined;
  quantityDisplayNumber: number | undefined;
  displayUnit: string;
} => {
  if (totalQuantity == null) {
    return {
      quantityPlaceholder: undefined,
      quantityDisplayNumber: undefined,
      displayUnit: unit,
    };
  }
  const mathJSUnit = isQuantityUnit(unit)
    ? quantityUnitToMathJSUnit(unit)
    : undefined;
  if (!mathJSUnit) {
    return {
      quantityPlaceholder: getDisplayNumber(totalQuantity),
      quantityDisplayNumber: totalQuantity,
      displayUnit: unit,
    };
  }
  const data = getDisplayValueData({
    value: totalQuantity,
    unit: mathJSUnit,
  });
  return {
    quantityPlaceholder: data.value,
    quantityDisplayNumber: data.valueAsNumber,
    displayUnit: data.unit,
  };
};
